// ─────────────────────────────────────────────────────────────────────────────
// The « Formation par groupes » flag (#812): a centre working entirely in individualized mode
// turns the whole Groupes area off — the group sub-menus of Enseignement and Planification,
// their screens, and the « Groupe » axis of the context toolbar. Backend declaration:
// backend/Parcours.Core/Features/GroupTrainingFeature.cs (DefaultEnabled = true — the flag is
// positive so the OFF state hides; see the polarity note there). Toggled at /core/features.
// ─────────────────────────────────────────────────────────────────────────────
import { isEnabledUnlessTurnedOff } from './student-portal-features';
import { SPECIALIST_DASHBOARD_PATH, SPECIALIST_SECTION_PATH } from './specialist-feature';

/** Mirrors `GroupTrainingFeature.KEY` — must match the backend declaration exactly. */
export const GROUP_TRAINING_FEATURE = 'GroupTraining';

/**
 * On unless explicitly turned off: the flag defaults to enabled, so the frame before the flag
 * load resolves — or a failed load — must not withdraw the whole Groupes area.
 * Pass `useSdk().Features.flags.value` so the reading stays reactive.
 */
export function isGroupTrainingEnabled(flags: Record<string, boolean>): boolean {
  return isEnabledUnlessTurnedOff(flags, GROUP_TRAINING_FEATURE);
}

/**
 * Where a route whose `meta.feature` flag is off must land, or `null` to let navigation
 * proceed. Menu entries are already hidden by the sidebar ACL; this covers what menus cannot —
 * deep links, bookmarks, and in-app links from screens that survive the flag (a group chip, a
 * report). The target is the section's own dashboard, so the user stays in familiar territory.
 *
 * `isEnabledUnlessTurnedOff` is the right reading here whatever the flag's declared default —
 * including an opt-in one such as `Demonstration` (`DefaultEnabled = false`). Only an explicit
 * `false` sends the user away, so the frame before the flags land never bounces someone off a
 * page they are entitled to; the guard's watch re-judges the standing route the moment the
 * catalog arrives, and the catalog carries every declared key with its effective value.
 */
export function resolveFeatureRedirect(
  to: { path: string; meta: Record<string, unknown> },
  flags: Record<string, boolean>
): string | null {
  const feature = to.meta.feature;
  if (typeof feature !== 'string' || isEnabledUnlessTurnedOff(flags, feature)) return null;
  if (to.path.startsWith('/planification')) return '/planification/tableau-de-bord';
  if (to.path.startsWith('/gestion')) return '/gestion/tableau-de-bord';
  // The specialist's dashboard carries no flag: it says « Fonctionnalité désactivée » itself (#1445).
  if (to.path.startsWith(SPECIALIST_SECTION_PATH)) return SPECIALIST_DASHBOARD_PATH;
  return '/enseignement/tableau-de-bord';
}
